import "pixi.js/unsafe-eval";

import {
  Application,
  Assets,
  Container,
  Graphics,
  Rectangle,
  Sprite,
  Text,
  Texture,
} from "pixi.js";

import { reactionGlyphs } from "../content/productCopy";
import { colorPalette } from "../design/productConfig";
import {
  billboardGraphemeCount,
  type BillboardDisplaySnapshot,
} from "../features/billboards/billboardDisplay";
import type { BillboardSlotId } from "../services/billboard-api/BillboardApi";
import { PAID_VEHICLE_PRESETS } from "../types/traffic";
import {
  advanceWrappedProgress,
  laneMotion,
  projectedRoadProgress,
  remotePresenceAgeSeconds,
  smoothstep,
} from "./motion";
import type {
  DriverProfile,
  Lane,
  PerformanceTier,
  Reaction,
  RemoteDriver,
  TrafficEvent,
  VehiclePreset,
} from "../types/traffic";

export type TrafficSceneSnapshot = {
  phase: "spectating" | "merging" | "driving" | "exiting" | "report";
  lane: Lane;
  targetSpeed: number;
  displayedSpeed: number;
  profile: DriverProfile | null;
  elapsedSeconds: number;
  distanceMiles: number;
  remoteDrivers: RemoteDriver[];
  relativeTripPositions: boolean;
  reducedMotion: boolean;
  performanceTier: PerformanceTier;
  hornSequence: number;
  flashSequence: number;
  reactionSequence: number;
  lastReaction: Reaction | null;
  boundarySequence: number;
  remoteEventSequence: number;
  lastRemoteEvent: TrafficEvent | null;
  billboards: BillboardDisplaySnapshot[];
  billboardsInteractive: boolean;
};

type VehicleEntity = {
  container: Container;
  sprite: Sprite;
  brakeLights: Graphics;
  leftTurnSignal: Graphics;
  rightTurnSignal: Graphics;
  lane: Lane;
  yProgress: number;
  baseProgress: number;
  targetY: number;
  displayY: number;
  onRoad: boolean;
  entryProgress: number;
  speedVariance: number;
  velocity: number;
  lanePosition: number;
  laneChangeFrom: number;
  laneChangeProgress: number;
  laneChangeDuration: number;
  exitProgress: number;
  missingSince: number | null;
  driver: RemoteDriver;
};

type LaneDash = {
  graphic: Graphics;
  boundary: number;
  progress: number;
};

const freeAtlasFrames: Partial<Record<VehiclePreset, number>> = {
  sedan: 0,
  hatchback: 1,
  "compact-suv": 2,
  minivan: 3,
};

const paidAtlasFrames: Partial<Record<VehiclePreset, number>> = {
  "retro-coupe": 0,
  "sports-coupe": 1,
  microcar: 2,
  "utility-wagon": 3,
  roadster: 4,
  "boxy-electric": 5,
};

function isPaidVehicle(preset: VehiclePreset): boolean {
  return PAID_VEHICLE_PRESETS.includes(
    preset as (typeof PAID_VEHICLE_PRESETS)[number],
  );
}

function hash(value: string): number {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return result >>> 0;
}

function easeInOut(value: number): number {
  const clamped = Math.max(0, Math.min(1, value));
  return clamped < 0.5
    ? 2 * clamped * clamped
    : 1 - Math.pow(-2 * clamped + 2, 2) / 2;
}

function snapshotDurationForLaneChange(sessionId: string): number {
  return 0.48 + (hash(sessionId) % 120) / 1_000;
}

function vehicleColor(colorId: string): number {
  return colorId in colorPalette
    ? colorPalette[colorId as keyof typeof colorPalette]
    : 0xff5a1f;
}

export class TrafficScene {
  private readonly app = new Application();
  private readonly world = new Container();
  private readonly background = new Graphics();
  private readonly skyline = new Graphics();
  private readonly road = new Graphics();
  private readonly laneLayer = new Container();
  private readonly sceneryLayer = new Container();
  private readonly vehicleLayer = new Container();
  private readonly effectsLayer = new Container();
  private readonly laneDashes: LaneDash[] = [];
  private readonly remoteEntities = new Map<string, VehicleEntity>();
  private readonly vehicleTextures = new Map<VehiclePreset, Texture>();
  private readonly laneBuckets: VehicleEntity[][] = [[], [], [], [], [], []];
  private readonly getSnapshot: () => TrafficSceneSnapshot;
  private readonly inspectRemote: (driver: RemoteDriver) => void;

  private atlasTexture: Texture | null = null;
  private paidAtlasTexture: Texture | null = null;
  private paidAtlasLoading: Promise<void> | null = null;
  private paidAtlasUnavailable = false;
  private userEntity: VehicleEntity | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private width = 0;
  private height = 0;
  private roadOffset = 0;
  private visualSpeed = 0;
  private visualAcceleration = 0;
  private userLane: Lane = 4;
  private userLanePosition = 4;
  private userLaneChangeFrom = 4;
  private userLaneChangeProgress = 1;
  private userLaneChangeDuration = 0.55;
  private userLaneStartY = 0;
  private userDisplayY = 0;
  private userMergeTargetY = 0;
  private mergeProgress = 0;
  private exitProgress = 0;
  private lastPhase: TrafficSceneSnapshot["phase"] = "spectating";
  private lastHornSequence = 0;
  private lastFlashSequence = 0;
  private lastReactionSequence = 0;
  private lastBoundarySequence = 0;
  private lastRemoteEventSequence = 0;
  private currentPerformanceTier: PerformanceTier | null = null;
  private lastVisibleRemoteCount = -1;
  private billboardKey = "";
  private initialized = false;
  private destroyed = false;

  public constructor(
    private readonly host: HTMLElement,
    getSnapshot: () => TrafficSceneSnapshot,
    inspectRemote: (driver: RemoteDriver) => void,
    private readonly reportVisibleRemoteCount: (count: number) => void,
    private readonly inspectBillboard: (slotId: BillboardSlotId) => void,
  ) {
    this.getSnapshot = getSnapshot;
    this.inspectRemote = inspectRemote;
  }

  public async init(): Promise<void> {
    const snapshot = this.getSnapshot();
    if (
      ((snapshot.profile && isPaidVehicle(snapshot.profile.vehiclePreset)) ||
        snapshot.remoteDrivers.some((driver) =>
          isPaidVehicle(driver.vehiclePreset),
        )) &&
      !this.paidAtlasUnavailable
    ) {
      void this.loadPaidAtlas();
    }
    const resolution =
      snapshot.performanceTier === "high"
        ? Math.min(devicePixelRatio, 2)
        : snapshot.performanceTier === "standard"
          ? Math.min(devicePixelRatio, 1.5)
          : Math.min(devicePixelRatio, 1.25);

    await this.app.init({
      backgroundAlpha: 0,
      antialias: snapshot.performanceTier !== "reduced",
      autoDensity: true,
      resolution,
      preference: "webgl",
      powerPreference:
        snapshot.performanceTier === "reduced"
          ? "low-power"
          : "high-performance",
    });
    this.initialized = true;

    if (this.destroyed) {
      this.initialized = false;
      this.app.destroy(true, { children: true });
      return;
    }

    this.host.append(this.app.canvas);
    this.app.canvas.setAttribute("aria-hidden", "true");
    this.app.stage.addChild(this.world);
    this.world.addChild(
      this.background,
      this.skyline,
      this.road,
      this.laneLayer,
      this.sceneryLayer,
      this.vehicleLayer,
      this.effectsLayer,
    );

    this.atlasTexture = await Assets.load<Texture>("/assets/vehicles-free.svg");
    if (this.wasDestroyed()) return;
    this.createLaneDashes();
    this.visualSpeed = snapshot.displayedSpeed;
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.host);
    this.resize();
    this.app.ticker.maxFPS = snapshot.performanceTier === "reduced" ? 30 : 60;
    this.currentPerformanceTier = snapshot.performanceTier;
    this.app.ticker.add((ticker) => this.update(ticker.deltaMS / 1000));
  }

  public destroy(): void {
    this.destroyed = true;
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.initialized) {
      this.initialized = false;
      this.app.destroy(true, { children: true });
    }
  }

  public setPaused(paused: boolean): void {
    if (paused) this.app.ticker.stop();
    else this.app.ticker.start();
  }

  private resize(): void {
    this.width = Math.max(1, this.host.clientWidth);
    this.height = Math.max(1, this.host.clientHeight);
    this.app.renderer.resize(this.width, this.height);
    const snapshot = this.getSnapshot();
    this.drawEnvironment(snapshot.billboards, snapshot.billboardsInteractive);
    this.userLane = snapshot.lane;
    this.userLanePosition = snapshot.lane;
    this.userLaneChangeFrom = snapshot.lane;
    this.userLaneChangeProgress = 1;
    this.userMergeTargetY = this.laneMergeY(snapshot.lane);
    this.userDisplayY = this.userMergeTargetY;
    this.userLaneStartY = this.userDisplayY;
    this.app.render();
  }

  private drawEnvironment(
    billboards: BillboardDisplaySnapshot[],
    billboardsInteractive: boolean,
  ): void {
    const { width, height } = this;
    const horizon = this.horizonY();
    this.background.clear();
    this.background.rect(0, 0, width, height).fill({ color: 0x0b0d0f });
    this.background.rect(0, 0, width, horizon + 90).fill({ color: 0x211f39 });
    this.background
      .rect(0, horizon * 0.35, width, horizon * 0.65)
      .fill({ color: 0x5b4b8a, alpha: 0.48 });
    this.background
      .circle(width * 0.78, horizon * 0.46, Math.min(width, height) * 0.075)
      .fill({ color: 0xffb36b, alpha: 0.82 });
    this.background
      .rect(0, horizon - 45, width, 90)
      .fill({ color: 0xff5a1f, alpha: 0.1 });

    this.skyline.clear();
    this.skyline
      .moveTo(0, horizon)
      .lineTo(0, horizon - 35)
      .lineTo(width * 0.08, horizon - 56)
      .lineTo(width * 0.15, horizon - 33)
      .lineTo(width * 0.24, horizon - 82)
      .lineTo(width * 0.31, horizon - 45)
      .lineTo(width * 0.41, horizon - 62)
      .lineTo(width * 0.5, horizon - 36)
      .lineTo(width * 0.63, horizon - 74)
      .lineTo(width * 0.76, horizon - 39)
      .lineTo(width * 0.88, horizon - 68)
      .lineTo(width, horizon - 30)
      .lineTo(width, horizon)
      .closePath()
      .fill({ color: 0x12151d });

    const topLeft = this.roadLeft(horizon);
    const topRight = this.roadRight(horizon);
    const bottomLeft = this.roadLeft(height);
    const bottomRight = this.roadRight(height);
    this.road.clear();
    this.road
      .moveTo(topLeft - 24, horizon)
      .lineTo(topRight + 24, horizon)
      .lineTo(bottomRight + 110, height)
      .lineTo(bottomLeft - 110, height)
      .closePath()
      .fill({ color: 0x14181c });
    this.road
      .moveTo(topLeft, horizon)
      .lineTo(topRight, horizon)
      .lineTo(bottomRight, height)
      .lineTo(bottomLeft, height)
      .closePath()
      .fill({ color: 0x24282b });
    this.road
      .moveTo(topLeft, horizon)
      .lineTo(bottomLeft, height)
      .stroke({ color: 0xb8bcc2, width: 4, alpha: 0.7 });
    this.road
      .moveTo(topRight, horizon)
      .lineTo(bottomRight, height)
      .stroke({ color: 0xb8bcc2, width: 4, alpha: 0.7 });

    this.drawBillboards(billboards, billboardsInteractive);
  }

  private drawBillboards(
    billboards: BillboardDisplaySnapshot[],
    interactive: boolean,
  ): void {
    this.sceneryLayer.removeChildren().forEach((child) => child.destroy());
    this.billboardKey = this.billboardSnapshotKey(billboards, interactive);
    const horizon = this.horizonY();
    const travel = this.height - horizon;
    const fallbackScale = Math.max(0.72, Math.min(1, this.width / 900));
    const positions = [
      { progress: 0.14, side: "right", scale: 0.5, textBoost: 1 },
      { progress: 0.29, side: "left", scale: 0.62, textBoost: 1.08 },
      { progress: 0.49, side: "right", scale: 0.86, textBoost: 1.3 },
    ] as const;

    billboards.forEach((billboard, index) => {
      const position = positions[index];
      if (!position) return;
      const y = horizon + travel * position.progress;
      const scale = position.scale * fallbackScale;
      const sign = this.createBillboard(
        billboard,
        interactive,
        scale,
        position.textBoost,
      );
      const boardHalfWidth = 120 * scale;
      const roadsideGap = 18 + 35 * scale;
      const desiredX =
        position.side === "left"
          ? this.roadLeft(y) - boardHalfWidth - roadsideGap
          : this.roadRight(y) + boardHalfWidth + roadsideGap;
      const x = Math.max(
        boardHalfWidth + 4,
        Math.min(this.width - boardHalfWidth - 4, desiredX),
      );
      sign.position.set(x, y);
      sign.scale.set(scale);
      sign.zIndex = y;
      this.sceneryLayer.addChild(sign);
    });
    this.sceneryLayer.sortableChildren = true;
    this.sceneryLayer.sortChildren();
  }

  private createBillboard(
    billboard: BillboardDisplaySnapshot,
    interactive: boolean,
    displayScale: number,
    textBoost: number,
  ): Container {
    const sign = new Container();
    const post = new Graphics().rect(-5, 40, 10, 102).fill({ color: 0x747b81 });
    const shadow = new Graphics()
      .roundRect(-124, -43, 248, 88, 5)
      .fill({ color: 0x050607, alpha: 0.38 });
    const board = new Graphics()
      .roundRect(-120, -40, 240, 80, 3)
      .fill({ color: 0x11151a })
      .stroke({ color: 0xf4f1e8, width: 2, alpha: 0.86 });
    const header = new Graphics()
      .rect(-120, -40, 240, 18)
      .fill({ color: 0xff5a1f });
    const headerFontSize = Math.max(8, 5.5 / displayScale);
    const paidLabel = new Text({
      text: billboard.isHouseAd ? "ROAD NOTICE" : "PAID AD",
      style: {
        fill: 0x0b0d0f,
        fontFamily: "Space Grotesk, Inter, sans-serif",
        fontSize: headerFontSize,
        fontWeight: "800",
        letterSpacing: 1.1,
      },
    });
    paidLabel.anchor.set(0, 0.5);
    paidLabel.position.set(-111, -31);
    const cadenceLabel = new Text({
      text: billboard.cadenceLabel,
      style: {
        fill: 0x0b0d0f,
        fontFamily: "Space Grotesk, Inter, sans-serif",
        fontSize: headerFontSize,
        fontWeight: "800",
        letterSpacing: 1.1,
      },
    });
    cadenceLabel.anchor.set(1, 0.5);
    cadenceLabel.position.set(111, -31);
    const lines = this.fitBillboardLines(billboard.message);
    const longestLine = Math.max(
      ...lines.split("\n").map((line) => billboardGraphemeCount(line)),
    );
    const baseFontSize =
      (longestLine > 32 ? 9 : longestLine > 24 ? 10.5 : 13) * textBoost;
    const fontSize = Math.max(baseFontSize, 7.5 / displayScale);
    const copy = new Text({
      text: lines,
      style: {
        align: "center",
        fill: 0xf4f1e8,
        fontFamily: "Space Grotesk, Inter, sans-serif",
        fontSize,
        fontWeight: "700",
        letterSpacing: 0.8,
        lineHeight: fontSize * 1.12,
      },
    });
    copy.anchor.set(0.5);
    copy.position.set(0, 8);
    copy.scale.set(Math.min(1, 216 / copy.width, 48 / copy.height));
    sign.addChild(post, shadow, board, header, paidLabel, cadenceLabel, copy);
    if (interactive) {
      sign.eventMode = "static";
      sign.cursor = "pointer";
      sign.hitArea = new Rectangle(-120, -40, 240, 80);
      sign.on("pointertap", () => this.inspectBillboard(billboard.slotId));
    }
    return sign;
  }

  private fitBillboardLines(message: string): string {
    const normalized = message
      .replace(/\r\n?/g, "\n")
      .split("\n")
      .slice(0, 2)
      .map((line) => line.replace(/\s+/g, " ").trim())
      .filter(Boolean);
    if (normalized.length > 1) return normalized.join("\n");
    const line = normalized[0] ?? "YOUR AD HERE";
    const graphemes = Array.from(
      new Intl.Segmenter("en", { granularity: "grapheme" }).segment(line),
      ({ segment }) => segment,
    );
    if (graphemes.length <= 24) return line;
    const midpoint = Math.floor(graphemes.length / 2);
    const nearbySpace = graphemes
      .map((value, index) => ({ value, index }))
      .filter(({ value }) => value === " ")
      .sort(
        (first, second) =>
          Math.abs(first.index - midpoint) - Math.abs(second.index - midpoint),
      )[0]?.index;
    const splitAt = nearbySpace ?? midpoint;
    return `${graphemes.slice(0, splitAt).join("").trim()}\n${graphemes
      .slice(splitAt + (nearbySpace === undefined ? 0 : 1))
      .join("")
      .trim()}`;
  }

  private billboardSnapshotKey(
    billboards: BillboardDisplaySnapshot[],
    interactive: boolean,
  ): string {
    return `${interactive}:${billboards
      .map(
        (billboard) =>
          `${billboard.slotId}:${billboard.placementId ?? "house"}:${billboard.message}:${billboard.destinationUrl ?? ""}:${billboard.isHouseAd}`,
      )
      .join("|")}`;
  }

  private createLaneDashes(): void {
    for (let boundary = 1; boundary <= 5; boundary += 1) {
      for (let segment = 0; segment < 10; segment += 1) {
        const graphic = new Graphics()
          .roundRect(-2, -22, 4, 44, 2)
          .fill({ color: 0xf4f1e8, alpha: 0.8 });
        this.laneLayer.addChild(graphic);
        this.laneDashes.push({
          graphic,
          boundary,
          progress: (segment + boundary * 0.09) / 10,
        });
      }
    }
  }

  private update(deltaSeconds: number): void {
    if (this.width <= 1 || deltaSeconds <= 0 || deltaSeconds > 0.2) return;
    const snapshot = this.getSnapshot();
    const billboardKey = this.billboardSnapshotKey(
      snapshot.billboards,
      snapshot.billboardsInteractive,
    );
    if (billboardKey !== this.billboardKey) {
      this.drawBillboards(snapshot.billboards, snapshot.billboardsInteractive);
    }
    if (
      ((snapshot.profile && isPaidVehicle(snapshot.profile.vehiclePreset)) ||
        snapshot.remoteDrivers.some((driver) =>
          isPaidVehicle(driver.vehiclePreset),
        )) &&
      !this.paidAtlasUnavailable
    ) {
      void this.loadPaidAtlas();
    }
    if (snapshot.performanceTier !== this.currentPerformanceTier) {
      this.currentPerformanceTier = snapshot.performanceTier;
      this.app.ticker.maxFPS = snapshot.performanceTier === "reduced" ? 30 : 60;
    }
    const motionScale = snapshot.reducedMotion ? 0.25 : 1;
    const previousVisualSpeed = this.visualSpeed;
    const speedFollow = 1 - Math.exp(-deltaSeconds * 3.5);
    this.visualSpeed +=
      (snapshot.displayedSpeed - this.visualSpeed) * speedFollow;
    this.visualAcceleration =
      (this.visualSpeed - previousVisualSpeed) / Math.max(deltaSeconds, 0.001);
    this.roadOffset =
      (this.roadOffset +
        deltaSeconds * (0.006 + this.visualSpeed * 0.0022) * motionScale) %
      1;

    this.updateLaneDashes();
    this.syncRemoteVehicles(snapshot.remoteDrivers);
    this.updateUserVehicle(deltaSeconds, snapshot);
    this.updateRemoteVehicles(deltaSeconds, snapshot);
    this.updateEffects(snapshot);
    this.lastPhase = snapshot.phase;
  }

  private updateLaneDashes(): void {
    const horizon = this.horizonY();
    const roadLength = this.height - horizon;
    for (const dash of this.laneDashes) {
      const progress = (dash.progress + this.roadOffset) % 1;
      const curvedProgress = projectedRoadProgress(progress);
      const y = horizon + curvedProgress * roadLength;
      const left = this.roadLeft(y);
      const right = this.roadRight(y);
      dash.graphic.position.set(left + ((right - left) * dash.boundary) / 6, y);
      const sampleTop = Math.max(horizon, y - 12);
      const sampleBottom = Math.min(this.height, y + 12);
      const sampleTopX =
        this.roadLeft(sampleTop) +
        (this.roadWidthAt(sampleTop) * dash.boundary) / 6;
      const sampleBottomX =
        this.roadLeft(sampleBottom) +
        (this.roadWidthAt(sampleBottom) * dash.boundary) / 6;
      dash.graphic.rotation = -Math.atan2(
        sampleBottomX - sampleTopX,
        Math.max(1, sampleBottom - sampleTop),
      );
      const scale = 0.24 + progress * 1.25;
      dash.graphic.scale.set(scale, scale);
      dash.graphic.alpha = 0.25 + progress * 0.7;
    }
  }

  private syncRemoteVehicles(drivers: RemoteDriver[]): void {
    const activeIds = new Set(drivers.map((driver) => driver.sessionId));
    const now = performance.now();
    for (const [sessionId, entity] of this.remoteEntities) {
      if (activeIds.has(sessionId)) {
        entity.missingSince = null;
        continue;
      }
      if (entity.driver.departing) {
        if (entity.exitProgress >= 1) {
          this.remoteEntities.delete(sessionId);
          entity.container.destroy({ children: true });
        }
        continue;
      }
      entity.missingSince ??= now;
      if (now - entity.missingSince > 2_800) {
        this.remoteEntities.delete(sessionId);
        entity.container.destroy({ children: true });
      }
    }

    for (const driver of drivers) {
      const existing = this.remoteEntities.get(driver.sessionId);
      if (existing) {
        if (existing.driver.vehiclePreset !== driver.vehiclePreset) {
          existing.sprite.texture = this.vehicleTexture(driver.vehiclePreset);
        }
        if (existing.driver.colorId !== driver.colorId) {
          existing.sprite.tint = vehicleColor(driver.colorId);
        }
        existing.missingSince = null;
        if (existing.lane !== driver.lane) {
          existing.laneChangeFrom = existing.lanePosition;
          existing.laneChangeProgress = 0;
          existing.laneChangeDuration = snapshotDurationForLaneChange(
            driver.sessionId,
          );
          existing.lane = driver.lane;
        }
        existing.driver = {
          ...driver,
          sessionDistanceMiles: Math.max(
            existing.driver.sessionDistanceMiles,
            driver.sessionDistanceMiles,
          ),
        };
        if (!driver.departing) existing.exitProgress = 0;
        continue;
      }
      const seed = hash(driver.sessionId);
      const entity = this.createVehicleEntity(driver);
      entity.baseProgress = 0.12 + (seed % 820) / 1000;
      entity.yProgress = entity.baseProgress;
      entity.displayY = Number.NaN;
      entity.entryProgress = (seed % 45) / 100;
      entity.speedVariance = ((seed >> 8) % 19) / 100 - 0.09;
      entity.lane = driver.lane;
      entity.lanePosition = driver.lane;
      entity.laneChangeFrom = driver.lane;
      entity.container.eventMode = "static";
      entity.container.cursor = "pointer";
      entity.container.on("pointertap", () =>
        this.inspectRemote(entity.driver),
      );
      this.remoteEntities.set(driver.sessionId, entity);
      this.vehicleLayer.addChild(entity.container);
    }
  }

  private createVehicleEntity(driver: RemoteDriver): VehicleEntity {
    const sprite = new Sprite(this.vehicleTexture(driver.vehiclePreset));
    sprite.anchor.set(0.5);
    sprite.tint = vehicleColor(driver.colorId);

    const contactShadow = new Graphics()
      .ellipse(0, 18, 58, 92)
      .fill({ color: 0x050607, alpha: 0.34 });
    const brakeLights = new Graphics()
      .circle(-31, 72, 8)
      .circle(31, 72, 8)
      .fill({ color: 0xff304f });
    brakeLights.alpha = 0.45;
    const leftTurnSignal = new Graphics()
      .circle(-39, 52, 7)
      .fill({ color: 0xffc247 });
    const rightTurnSignal = new Graphics()
      .circle(39, 52, 7)
      .fill({ color: 0xffc247 });
    leftTurnSignal.alpha = 0;
    rightTurnSignal.alpha = 0;

    const plate = new Text({
      text: driver.plate,
      style: {
        fill: 0x111317,
        fontFamily: "Space Grotesk, sans-serif",
        fontSize: 11,
        fontWeight: "700",
        letterSpacing: 0.6,
      },
    });
    plate.anchor.set(0.5);
    plate.position.set(0, 78);
    const container = new Container();
    container.addChild(
      contactShadow,
      sprite,
      brakeLights,
      leftTurnSignal,
      rightTurnSignal,
      plate,
    );

    return {
      container,
      sprite,
      brakeLights,
      leftTurnSignal,
      rightTurnSignal,
      lane: driver.lane,
      yProgress: 0,
      baseProgress: 0,
      targetY: 0,
      displayY: Number.NaN,
      onRoad: true,
      entryProgress: 0,
      speedVariance: 0,
      velocity: 0,
      lanePosition: driver.lane,
      laneChangeFrom: driver.lane,
      laneChangeProgress: 1,
      laneChangeDuration: snapshotDurationForLaneChange(driver.sessionId),
      exitProgress: 0,
      missingSince: null,
      driver,
    };
  }

  private remoteProgress(
    entity: VehicleEntity,
    snapshot: TrafficSceneSnapshot,
  ): number {
    if (!snapshot.relativeTripPositions || entity.driver.departing) {
      return entity.baseProgress;
    }

    const receivedAt = entity.driver.presenceReceivedAt ?? Date.now();
    const ageSeconds = remotePresenceAgeSeconds(Date.now(), receivedAt);
    const extrapolatedMiles =
      entity.driver.sessionDistanceMiles +
      (this.visualSpeed * ageSeconds) / 3600;
    const deltaMiles = extrapolatedMiles - snapshot.distanceMiles;
    const visibleSpanMiles = Math.max(
      0.08,
      (snapshot.displayedSpeed / 3600) * 14,
    );
    const jitter = ((hash(entity.driver.sessionId) % 17) - 8) / 1600;
    const offset = Math.max(
      -0.16,
      Math.min(0.16, deltaMiles / visibleSpanMiles),
    );
    return Math.max(0.3, Math.min(0.82, 0.56 - offset + jitter));
  }

  private updateRemoteVehicles(
    deltaSeconds: number,
    snapshot: TrafficSceneSnapshot,
  ): void {
    const horizon = this.horizonY();
    const travel = this.height - horizon;
    const congestion = 1 - snapshot.targetSpeed / 68;

    for (const entity of this.remoteEntities.values()) {
      if (!snapshot.relativeTripPositions && !entity.driver.departing) {
        const targetVelocity =
          (0.003 + (this.visualSpeed / 68) * 0.013) *
          (1 + entity.speedVariance);
        const velocityFollow = 1 - Math.exp(-deltaSeconds * 1.8);
        entity.velocity += (targetVelocity - entity.velocity) * velocityFollow;
        entity.baseProgress = advanceWrappedProgress(
          entity.baseProgress,
          entity.velocity,
          deltaSeconds,
        );
      }
      const progress = this.remoteProgress(entity, snapshot);
      entity.yProgress = progress;
      if (snapshot.relativeTripPositions) entity.baseProgress = progress;
      entity.targetY =
        horizon + projectedRoadProgress(Math.min(1, progress)) * travel;
      const braking = Math.max(0, -this.visualAcceleration) / 18;
      entity.brakeLights.alpha = Math.min(
        1,
        0.18 + congestion * 0.28 + braking,
      );
    }

    this.spaceRemoteVehicles(snapshot);
    const follow = 1 - Math.exp(-deltaSeconds * 7);
    for (const entity of this.remoteEntities.values()) {
      if (!Number.isFinite(entity.displayY)) entity.displayY = entity.targetY;
      entity.displayY += (entity.targetY - entity.displayY) * follow;
      entity.laneChangeProgress = Math.min(
        1,
        entity.laneChangeProgress +
          deltaSeconds /
            (snapshot.reducedMotion ? 0.12 : entity.laneChangeDuration),
      );
      const laneFrame = laneMotion(
        entity.laneChangeFrom,
        entity.lane,
        entity.laneChangeProgress,
      );
      const signalPulse =
        entity.laneChangeProgress < 1
          ? 0.35 +
            Math.pow(Math.sin(entity.laneChangeProgress * Math.PI * 3), 2) *
              0.65
          : 0;
      entity.leftTurnSignal.alpha =
        entity.lane < entity.laneChangeFrom ? signalPulse : 0;
      entity.rightTurnSignal.alpha =
        entity.lane > entity.laneChangeFrom ? signalPulse : 0;
      entity.lanePosition = laneFrame.lanePosition;
      const targetX = this.laneXAtPosition(
        entity.lanePosition,
        entity.displayY,
      );
      entity.entryProgress = Math.min(
        1,
        entity.entryProgress + deltaSeconds / (0.7 + congestion * 0.45),
      );
      const entryEase = easeInOut(entity.entryProgress);
      if (entity.entryProgress < 1) {
        const entryX =
          entity.lane <= 3
            ? this.roadLeft(entity.displayY) - 48
            : this.roadRight(entity.displayY) + 48;
        entity.container.x = entryX + (targetX - entryX) * entryEase;
      } else {
        entity.container.x = targetX;
      }
      entity.container.y = entity.displayY;
      entity.container.rotation =
        this.laneTangent(entity.lanePosition, entity.displayY) +
        laneFrame.steeringYaw;
      if (entity.driver.departing) {
        entity.exitProgress = Math.min(
          1,
          entity.exitProgress +
            deltaSeconds / (snapshot.reducedMotion ? 0.35 : 1.65),
        );
        const eased = easeInOut(entity.exitProgress);
        entity.container.x += eased * this.width * 0.42;
        entity.container.y -= eased * this.height * 0.28;
        entity.container.rotation = -eased * 0.22;
      }
      entity.container.zIndex = entity.displayY;
      const displayProgress = Math.sqrt(
        Math.max(
          0,
          Math.min(1, (entity.displayY - horizon) / Math.max(1, travel)),
        ),
      );
      const scale = 0.12 + displayProgress * 0.35;
      entity.container.scale.set(scale);
      const horizonFade = Math.max(
        0,
        Math.min(1, (entity.displayY - horizon - 14) / 30),
      );
      const exitFade = entity.driver.departing
        ? 1 - Math.max(0, (entity.exitProgress - 0.7) / 0.3)
        : 1;
      entity.container.alpha = entryEase * horizonFade * exitFade;
      entity.container.visible = entity.onRoad && entity.container.alpha > 0.02;
    }
    const visibleRemoteCount = [...this.remoteEntities.values()].filter(
      (entity) => entity.container.visible && entity.driver.departing !== true,
    ).length;
    if (visibleRemoteCount !== this.lastVisibleRemoteCount) {
      this.lastVisibleRemoteCount = visibleRemoteCount;
      this.reportVisibleRemoteCount(visibleRemoteCount);
    }
    this.vehicleLayer.sortableChildren = true;
    this.vehicleLayer.sortChildren();
  }

  private spaceRemoteVehicles(snapshot: TrafficSceneSnapshot): void {
    for (const bucket of this.laneBuckets) bucket.length = 0;
    for (const entity of this.remoteEntities.values()) {
      this.laneBuckets[entity.lane - 1]?.push(entity);
    }

    this.laneBuckets.forEach((bucket, laneIndex) => {
      const lane = (laneIndex + 1) as Lane;
      const mergeY = this.userMergeTargetY || this.laneMergeY(lane);
      const minimumY = this.horizonY() + 18;
      const maximumY = this.height - (this.width < 700 ? 112 : 132);
      const congestion = Math.max(
        0,
        Math.min(1, 1 - snapshot.targetSpeed / 68),
      );
      const userVisible = ["merging", "driving", "exiting"].includes(
        snapshot.phase,
      );
      for (const entity of bucket) entity.onRoad = true;

      bucket.sort((a, b) => a.baseProgress - b.baseProgress);
      let previousY = Number.NEGATIVE_INFINITY;
      for (const entity of bucket) {
        if (entity.driver.departing) continue;
        const gap = this.remoteGapAt(entity.targetY, congestion);
        entity.targetY = Math.max(entity.targetY, previousY + gap);
        entity.onRoad =
          entity.targetY >= minimumY && entity.targetY <= maximumY;
        previousY = entity.targetY;
      }

      if (!userVisible || lane !== this.userLane) return;
      const visibleCars = bucket.filter(
        (entity) => entity.onRoad && !entity.driver.departing,
      );
      const ahead = visibleCars
        .filter((entity) => entity.targetY < mergeY)
        .at(-1);
      const behind = visibleCars.find((entity) => entity.targetY >= mergeY);
      const desiredClearance = 124;
      const maximumSqueeze = 48;

      if (ahead) {
        const squeeze = Math.min(
          maximumSqueeze,
          Math.max(0, desiredClearance - (mergeY - ahead.targetY)),
        );
        ahead.targetY -= squeeze;
      }
      if (behind) {
        const squeeze = Math.min(
          maximumSqueeze,
          Math.max(0, desiredClearance - (behind.targetY - mergeY)),
        );
        behind.targetY += squeeze;
      }
    });
  }

  private updateUserVehicle(
    deltaSeconds: number,
    snapshot: TrafficSceneSnapshot,
  ): void {
    if (
      this.userEntity &&
      snapshot.profile &&
      (this.userEntity.driver.vehiclePreset !==
        snapshot.profile.vehiclePreset ||
        this.userEntity.driver.colorId !== snapshot.profile.colorId)
    ) {
      this.userEntity.sprite.texture = this.vehicleTexture(
        snapshot.profile.vehiclePreset,
      );
      this.userEntity.sprite.tint = vehicleColor(snapshot.profile.colorId);
      this.userEntity.driver = {
        ...this.userEntity.driver,
        vehiclePreset: snapshot.profile.vehiclePreset,
        colorId: snapshot.profile.colorId,
      };
    }
    if (snapshot.profile && !this.userEntity) {
      this.userEntity = this.createVehicleEntity({
        sessionId: "local-user",
        publicDriverId: snapshot.profile.publicDriverId,
        lane: snapshot.lane,
        joinedAt: new Date().toISOString(),
        vehiclePreset: snapshot.profile.vehiclePreset,
        colorId: snapshot.profile.colorId,
        plate: snapshot.profile.plate,
        badge: snapshot.profile.badge,
        sessionElapsedSeconds: snapshot.elapsedSeconds,
        sessionDistanceMiles: snapshot.profile.odometerMiles,
        location: null,
      });
      const markerBoard = new Graphics()
        .roundRect(-42, -166, 84, 30, 4)
        .fill({ color: 0x102c30, alpha: 0.94 })
        .stroke({ color: 0x43dde6, width: 3 });
      const markerText = new Text({
        text: "YOU",
        style: {
          fill: 0x43dde6,
          fontFamily: "Space Grotesk, sans-serif",
          fontSize: 18,
          fontWeight: "700",
          letterSpacing: 2,
        },
      });
      markerText.anchor.set(0.5);
      markerText.position.set(0, -151);
      this.userEntity.container.addChild(markerBoard, markerText);
      this.userEntity.container.eventMode = "none";
      this.vehicleLayer.addChild(this.userEntity.container);
    }

    const user = this.userEntity;
    if (!user) return;
    const visible = ["merging", "driving", "exiting"].includes(snapshot.phase);
    user.container.visible = visible;
    if (!visible) return;

    if (snapshot.phase !== this.lastPhase) {
      if (snapshot.phase === "merging") {
        this.mergeProgress = 0;
        this.userLane = snapshot.lane;
        this.userLanePosition = snapshot.lane;
        this.userLaneChangeFrom = snapshot.lane;
        this.userLaneChangeProgress = 1;
        this.userMergeTargetY = this.findNearestLaneGap(snapshot.lane);
        this.userDisplayY = this.userMergeTargetY;
        this.userLaneStartY = this.userDisplayY;
      }
      if (snapshot.phase === "exiting") this.exitProgress = 0;
    }
    if (snapshot.lane !== this.userLane) {
      this.userLaneChangeFrom = this.userLanePosition;
      this.userLaneChangeProgress = 0;
      this.userLaneChangeDuration = snapshot.reducedMotion ? 0.12 : 0.55;
      this.userLaneStartY =
        this.userDisplayY || this.userMergeTargetY || this.userY();
      const gapY = this.findNearestLaneGap(snapshot.lane);
      this.userMergeTargetY = Math.max(
        this.userLaneStartY - 36,
        Math.min(this.userLaneStartY + 36, gapY),
      );
      this.userLane = snapshot.lane;
    }

    this.userLaneChangeProgress = Math.min(
      1,
      this.userLaneChangeProgress + deltaSeconds / this.userLaneChangeDuration,
    );
    const laneFrame = laneMotion(
      this.userLaneChangeFrom,
      this.userLane,
      this.userLaneChangeProgress,
    );
    const signalPulse =
      this.userLaneChangeProgress < 1
        ? 0.35 +
          Math.pow(Math.sin(this.userLaneChangeProgress * Math.PI * 3), 2) *
            0.65
        : 0;
    user.leftTurnSignal.alpha =
      this.userLane < this.userLaneChangeFrom ? signalPulse : 0;
    user.rightTurnSignal.alpha =
      this.userLane > this.userLaneChangeFrom ? signalPulse : 0;
    this.userLanePosition = laneFrame.lanePosition;
    const laneEase = smoothstep(this.userLaneChangeProgress);
    this.userDisplayY =
      this.userLaneStartY +
      (this.userMergeTargetY - this.userLaneStartY) * laneEase;
    const targetY = this.userDisplayY || this.laneMergeY(snapshot.lane);
    const targetX = this.laneXAtPosition(this.userLanePosition, targetY);
    let y = targetY;
    let x = targetX;
    let rotation =
      this.laneTangent(this.userLanePosition, targetY) + laneFrame.steeringYaw;
    let alpha = 1;

    if (snapshot.phase === "merging") {
      this.mergeProgress = Math.min(
        1,
        this.mergeProgress +
          deltaSeconds / (snapshot.reducedMotion ? 0.25 : 1.4),
      );
      const eased = easeInOut(this.mergeProgress);
      y = this.height + 150 + (targetY - this.height - 150) * eased;
      x =
        this.roadRight(this.height) +
        110 +
        (targetX - this.roadRight(this.height) - 110) * eased;
      rotation = -0.18 * (1 - eased);
    }

    if (snapshot.phase === "exiting") {
      this.exitProgress = Math.min(
        1,
        this.exitProgress +
          deltaSeconds / (snapshot.reducedMotion ? 0.35 : 1.75),
      );
      const eased = easeInOut(this.exitProgress);
      x += eased * (this.width * 0.42);
      y -= eased * this.height * 0.32;
      rotation = -eased * 0.22;
      alpha = 1 - Math.max(0, (eased - 0.76) / 0.24);
    }

    const userScale = Math.max(0.34, Math.min(0.52, this.width / 920));
    user.container.position.set(x, y);
    user.container.scale.set(userScale);
    user.container.rotation = rotation;
    user.container.alpha = alpha;
    user.container.zIndex = y + 10;
    const congestion = 1 - snapshot.targetSpeed / 68;
    const braking = Math.max(0, -this.visualAcceleration) / 16;
    user.brakeLights.alpha = Math.min(1, 0.18 + congestion * 0.28 + braking);
  }

  private updateEffects(snapshot: TrafficSceneSnapshot): void {
    if (
      snapshot.remoteEventSequence !== this.lastRemoteEventSequence &&
      snapshot.lastRemoteEvent
    ) {
      this.lastRemoteEventSequence = snapshot.remoteEventSequence;
      const remote = this.remoteEntities.get(
        snapshot.lastRemoteEvent.sessionId,
      );
      if (remote) {
        if (snapshot.lastRemoteEvent.type === "honk") {
          this.spawnRing(remote);
        } else if (snapshot.lastRemoteEvent.type === "flash") {
          this.spawnFlash(remote);
        } else if (snapshot.lastRemoteEvent.type === "reaction") {
          this.spawnReaction(
            remote.container.x,
            remote.container.y,
            snapshot.lastRemoteEvent.reaction,
          );
        }
      }
    }

    const user = this.userEntity;
    if (!user) return;

    if (snapshot.hornSequence !== this.lastHornSequence) {
      this.lastHornSequence = snapshot.hornSequence;
      this.spawnRing(user);
    }
    if (snapshot.flashSequence !== this.lastFlashSequence) {
      this.lastFlashSequence = snapshot.flashSequence;
      this.spawnFlash(user);
    }
    if (
      snapshot.reactionSequence !== this.lastReactionSequence &&
      snapshot.lastReaction
    ) {
      this.lastReactionSequence = snapshot.reactionSequence;
      this.spawnReaction(
        user.container.x,
        user.container.y,
        snapshot.lastReaction,
      );
    }
    if (snapshot.boundarySequence !== this.lastBoundarySequence) {
      this.lastBoundarySequence = snapshot.boundarySequence;
      this.spawnBoundaryMarker(user.container.x, user.container.y);
    }
  }

  private spawnRing(user: VehicleEntity): void {
    const ring = new Graphics()
      .circle(0, 0, 92)
      .stroke({ color: 0xffc247, width: 7, alpha: 0.95 });
    ring.position.set(0, 6);
    user.container.addChild(ring);
    let elapsed = 0;
    const animate = (ticker: { deltaMS: number }): void => {
      elapsed += ticker.deltaMS / 1000;
      ring.scale.set(0.7 + elapsed * 2.6);
      ring.alpha = Math.max(0, 1 - elapsed / 0.72);
      const squash = Math.sin(Math.min(1, elapsed / 0.18) * Math.PI) * 0.06;
      user.sprite.scale.set(1 + squash, 1 - squash);
      if (elapsed >= 0.72) {
        this.app.ticker.remove(animate);
        user.sprite.scale.set(1);
        ring.destroy();
      }
    };
    this.app.ticker.add(animate);
  }

  private spawnFlash(user: VehicleEntity): void {
    const beam = new Graphics()
      .circle(-29, -84, 10)
      .circle(29, -84, 10)
      .fill({ color: 0xfff4c2, alpha: 0.95 })
      .moveTo(-39, -82)
      .lineTo(-94, -245)
      .lineTo(-9, -245)
      .closePath()
      .fill({ color: 0xfff4c2, alpha: 0.42 })
      .moveTo(39, -82)
      .lineTo(94, -245)
      .lineTo(9, -245)
      .closePath()
      .fill({ color: 0xfff4c2, alpha: 0.42 });
    beam.alpha = 0;
    user.container.addChild(beam);
    let elapsed = 0;
    const animate = (ticker: { deltaMS: number }): void => {
      elapsed += ticker.deltaMS / 1000;
      const firstPulse =
        elapsed < 0.18 ? Math.sin((elapsed / 0.18) * Math.PI) : 0;
      const secondElapsed = elapsed - 0.25;
      const secondPulse =
        secondElapsed >= 0 && secondElapsed < 0.2
          ? Math.sin((secondElapsed / 0.2) * Math.PI)
          : 0;
      beam.alpha = Math.max(firstPulse, secondPulse);
      if (elapsed >= 0.5) {
        this.app.ticker.remove(animate);
        beam.destroy();
      }
    };
    this.app.ticker.add(animate);
  }

  private spawnReaction(x: number, y: number, reaction: Reaction): void {
    const bubble = new Container();
    const label = new Text({
      text: reactionGlyphs[reaction],
      style: {
        fill: 0x111317,
        fontFamily: "Space Grotesk, sans-serif",
        fontSize: 15,
        fontWeight: "700",
        letterSpacing: 1,
      },
    });
    label.anchor.set(0.5);
    const board = new Graphics()
      .roundRect(-42, -19, 84, 38, 19)
      .fill({ color: 0xf4f1e8 })
      .stroke({ color: 0x111317, width: 2 });
    bubble.addChild(board, label);
    bubble.position.set(x, y - 95);
    this.effectsLayer.addChild(bubble);
    let elapsed = 0;
    const animate = (ticker: { deltaMS: number }): void => {
      elapsed += ticker.deltaMS / 1000;
      bubble.y -= ticker.deltaMS * 0.025;
      bubble.alpha = elapsed < 1 ? 1 : Math.max(0, 1 - (elapsed - 1) / 0.5);
      if (elapsed >= 1.5) {
        this.app.ticker.remove(animate);
        bubble.destroy({ children: true });
      }
    };
    this.app.ticker.add(animate);
  }

  private spawnBoundaryMarker(x: number, y: number): void {
    const marker = new Text({
      text: "NO MORE LANES",
      style: {
        fill: 0xffc247,
        fontFamily: "Space Grotesk, sans-serif",
        fontSize: 13,
        fontWeight: "700",
        letterSpacing: 1,
      },
    });
    marker.anchor.set(0.5);
    marker.position.set(x, y - 92);
    this.effectsLayer.addChild(marker);
    window.setTimeout(() => marker.destroy(), 650);
  }

  private horizonY(): number {
    return this.height * (this.width < 600 ? 0.2 : 0.24);
  }

  private userY(): number {
    return this.height * (this.width < 600 ? 0.61 : 0.63);
  }

  private laneMergeY(lane: Lane): number {
    return this.userY() + (lane - 3.5) * Math.min(22, this.height * 0.03);
  }

  private findNearestLaneGap(lane: Lane): number {
    const baseY = this.laneMergeY(lane);
    const minimumY = Math.max(this.horizonY() + 150, baseY - 88);
    const maximumY = Math.min(this.height - 145, baseY + 88);
    const laneCars = [...this.remoteEntities.values()].filter(
      (entity) =>
        entity.lane === lane &&
        entity.onRoad &&
        Number.isFinite(entity.displayY),
    );
    let bestY = baseY;
    let bestClearance = Number.NEGATIVE_INFINITY;

    for (let index = 0; index <= 8; index += 1) {
      const candidate = minimumY + ((maximumY - minimumY) * index) / 8;
      const clearance = laneCars.reduce(
        (closest, entity) =>
          Math.min(closest, Math.abs(entity.displayY - candidate)),
        Number.POSITIVE_INFINITY,
      );
      if (
        clearance > bestClearance ||
        (clearance === bestClearance &&
          Math.abs(candidate - baseY) < Math.abs(bestY - baseY))
      ) {
        bestY = candidate;
        bestClearance = clearance;
      }
    }

    return bestY;
  }

  private remoteGapAt(y: number, congestion: number): number {
    const depth = Math.max(0, Math.min(1, y / Math.max(1, this.height)));
    const openRoadGap = 26 + depth * 86;
    const gridlockGap = 18 + depth * 54;
    return openRoadGap + (gridlockGap - openRoadGap) * Math.max(0, congestion);
  }

  private roadWidthAt(y: number): number {
    const horizon = this.horizonY();
    const progress = Math.max(
      0,
      Math.min(1, (y - horizon) / Math.max(1, this.height - horizon)),
    );
    const maxWidth = Math.min(this.width * 0.92, 1120);
    const minWidth = Math.min(this.width * 0.24, 280);
    return minWidth + (maxWidth - minWidth) * progress;
  }

  private roadLeft(y: number): number {
    return this.width / 2 - this.roadWidthAt(y) / 2;
  }

  private roadRight(y: number): number {
    return this.width / 2 + this.roadWidthAt(y) / 2;
  }

  private laneX(lane: Lane, y: number): number {
    return this.laneXAtPosition(lane, y);
  }

  private laneXAtPosition(lanePosition: number, y: number): number {
    const left = this.roadLeft(y);
    return left + (this.roadWidthAt(y) * (lanePosition - 0.5)) / 6;
  }

  private laneTangent(lanePosition: number, y: number): number {
    const sampleTop = Math.max(this.horizonY(), y - 12);
    const sampleBottom = Math.min(this.height, y + 12);
    const topX = this.laneXAtPosition(lanePosition, sampleTop);
    const bottomX = this.laneXAtPosition(lanePosition, sampleBottom);
    return -Math.atan2(bottomX - topX, Math.max(1, sampleBottom - sampleTop));
  }

  private vehicleTexture(preset: VehiclePreset): Texture {
    const existing = this.vehicleTextures.get(preset);
    if (existing) return existing;
    if (!this.atlasTexture) throw new Error("Vehicle atlas is not loaded.");
    const paid = isPaidVehicle(preset);
    if (paid && !this.paidAtlasTexture) {
      return this.vehicleTexture("sedan");
    }
    const source = paid ? this.paidAtlasTexture : this.atlasTexture;
    const frameIndex = paid ? paidAtlasFrames[preset] : freeAtlasFrames[preset];
    if (!source || frameIndex === undefined) {
      return this.vehicleTexture("sedan");
    }
    const texture = new Texture({
      source: source.source,
      frame: new Rectangle(frameIndex * 180, 0, 180, 240),
    });
    this.vehicleTextures.set(preset, texture);
    return texture;
  }

  private async loadPaidAtlas(): Promise<void> {
    if (this.paidAtlasTexture || this.paidAtlasLoading) {
      await this.paidAtlasLoading;
      return;
    }
    this.paidAtlasLoading = Assets.load<Texture>("/assets/vehicles-paid.svg")
      .then((texture) => {
        if (this.wasDestroyed()) return;
        this.paidAtlasTexture = texture;
        for (const entity of this.remoteEntities.values()) {
          if (isPaidVehicle(entity.driver.vehiclePreset)) {
            entity.sprite.texture = this.vehicleTexture(
              entity.driver.vehiclePreset,
            );
          }
        }
        if (
          this.userEntity &&
          isPaidVehicle(this.userEntity.driver.vehiclePreset)
        ) {
          this.userEntity.sprite.texture = this.vehicleTexture(
            this.userEntity.driver.vehiclePreset,
          );
        }
      })
      .catch(() => {
        this.paidAtlasUnavailable = true;
      });
    try {
      await this.paidAtlasLoading;
    } finally {
      this.paidAtlasLoading = null;
    }
  }

  private wasDestroyed(): boolean {
    return this.destroyed;
  }
}
